import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Autocomplete,
  Typography,
  Divider,
  Tab,
  Tabs,
  TabPanel
} from '@mui/material';
import { Diary } from '../../types';
import apiClient from '../../services/apiClient';
import './DiaryForm.css';

interface DiaryFormProps {
  diary?: Diary | null;
  onSave: (diary: Diary) => void;
  onCancel: () => void;
  open?: boolean;
}

const DiaryForm: React.FC<DiaryFormProps> = ({ 
  diary, 
  onSave, 
  onCancel, 
  open = true 
}) => {
  const [formData, setFormData] = useState<any>(diary || {
    // Campi obbligatori base
    name: '',
    externalCode: '',
    executionUnitId: '',
    typeId: 2, // 2=tecnica, 3=laboratorio
    slotSize: 15, // minuti
    visibility: 30, // giorni
    minAge: 0, // in mesi
    maxAge: 1200, // in mesi (100 anni)
    
    // Campi opzionali principali
    code: '',
    description: '',
    authorityId: '',
    hospitalId: '',
    siteId: '',
    operationUnitId: '',
    executionUnitIdLab: '',
    
    // Accesso e tipologia
    accessModeId: -1, // -1=n/a, 0=piedi, 1=macchina
    accessTypeId: -1, // 1=primo, 0=successivo, -1=entrambi
    online: false,
    privateFlag: false,
    extraCup: false,
    
    // Disponibilità
    availabilityDelay: 0,
    maxAvailabilityHours: 24,
    defOrderDurationMins: 5,
    
    // Coperture e diagnosi
    allCovers: true,
    coverIds: [],
    diagnosis: [],
    diaryAsls: [],
    
    // Configurazioni speciali
    freeForceEnabled: false,
    freeForceMaxDistance: 0,
    freeForceMinDistance: 0,
    freeForceMaxNumber: 0,
    freeForceNotesNeeded: false,
    freeForcePlaceHold: false,
    defaultFreeBookingDate: false,
    
    // Residenti e liste attesa
    forResidents: false,
    isWlReserved: false,
    wlReservedExpire: 0,
    
    // Overbook e slot
    overbook: false,
    onlyAdjacentSlots: false,
    isInFirstAvail: true,
    
    // Notifiche
    reminderAnonymization: false,
    
    // Laboratorio
    deliveryPlaceId: '',
    
    // Regime interno
    hospitalWardId: '',
    requestWards: [],
    
    // Esenzioni e progetti
    exemptionGroupIds: [],
    specialProjectsIds: [],
    
    // Array obbligatori (verranno gestiti separatamente)
    diarySupplyModes: [],
    orders: [],
    priorityClasses: [],
    doctors: [],
    resources: [],
    
    // Date
    startDate: '',
    endDate: '',
    status: 'active'
  });

  const [error, setError] = useState<string | null>(null);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Dati per le dropdown
  const [executionUnits, setExecutionUnits] = useState<any[]>([]);
  const [authorities, setAuthorities] = useState<any[]>([]);
  const [hospitals, setHospitals] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [supplyModes, setSupplyModes] = useState<any[]>([]);
  const [priorityClasses, setPriorityClasses] = useState<any[]>([]);
  const [availableExams, setAvailableExams] = useState<any[]>([]);
  const [availableDoctors, setAvailableDoctors] = useState<any[]>([]);
  
  // Carica dati per i dropdown all'apertura della modale
  useEffect(() => {
    console.log('📊 DiaryForm: useEffect triggered, open =', open);
    if (open) {
      console.log('📊 DiaryForm: Modal is open, calling loadFormData...');
      loadFormData();
      setDefaultDates();
    }
  }, [open]);
  
  const loadFormData = async () => {
    try {
      setLoading(true);
      setLoadingError(null);
      
      console.log('📊 DiaryForm: Caricamento dati form agenda...');
      
      // Recupera tutti i dati necessari dalle API usando apiClient
      const response = await apiClient.getDiaryFormData();
      console.log('📊 DiaryForm: Response received:', response);
      const result = response.data;
      console.log('📊 DiaryForm: Result data:', result);
      
      if (result.success) {
        const data = result.data;
        
        setExecutionUnits(data.executionUnits || []);
        setAuthorities(data.authorities || []);
        setHospitals(data.hospitals || []);
        setSites(data.sites || []);
        setSupplyModes(data.supplyModes || []);
        setPriorityClasses(data.priorityClasses || []);
        setAvailableExams(data.exams || []);
        setAvailableDoctors(data.doctors || []);
        
        console.log('Dati form caricati:', {
          executionUnits: data.executionUnits?.length || 0,
          authorities: data.authorities?.length || 0,
          hospitals: data.hospitals?.length || 0,
          sites: data.sites?.length || 0,
          supplyModes: data.supplyModes?.length || 0,
          priorityClasses: data.priorityClasses?.length || 0,
          exams: data.exams?.length || 0,
          doctors: data.doctors?.length || 0
        });
      } else {
        throw new Error(result.error || 'Errore nel caricamento dei dati');
      }
      
    } catch (error) {
      console.error('❌ DiaryForm: Errore caricamento dati form:', error);
      console.error('❌ DiaryForm: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setLoadingError('Errore nel caricamento dei dati per il form. Alcuni campi potrebbero non essere disponibili.');
    } finally {
      setLoading(false);
    }
  };
  
  const setDefaultDates = () => {
    const today = new Date();
    const endOfYear = new Date(today.getFullYear(), 11, 31);
    
    setFormData(prev => ({
      ...prev,
      startDate: today.toISOString().split('T')[0],
      endDate: endOfYear.toISOString().split('T')[0]
    }));
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    setError(null);
  };
  
  const handleArrayChange = (field: string, values: any[]) => {
    setFormData((prev: any) => ({ ...prev, [field]: values }));
    setError(null);
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSubmit = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    console.log('📝 DiaryForm: handleSubmit called with formData:', formData);
    
    // Validazione campi obbligatori
    console.log('📝 DiaryForm: Validating fields...');
    
    if (!formData.name) {
      console.log('❌ Validation failed: name missing');
      setError('Nome agenda è obbligatorio');
      return;
    }
    if (!formData.externalCode) {
      console.log('❌ Validation failed: externalCode missing');
      setError('Codice esterno è obbligatorio');
      return;
    }
    if (!formData.executionUnitId) {
      console.log('❌ Validation failed: executionUnitId missing:', formData.executionUnitId);
      setError('Unità esecutiva è obbligatoria');
      return;
    }
    if (!formData.typeId) {
      console.log('❌ Validation failed: typeId missing');
      setError('Tipo agenda è obbligatorio');
      return;
    }
    if (!formData.slotSize || formData.slotSize <= 0) {
      console.log('❌ Validation failed: slotSize invalid:', formData.slotSize);
      setError('Dimensione slot deve essere maggiore di 0');
      return;
    }
    if (!formData.visibility || formData.visibility <= 0) {
      console.log('❌ Validation failed: visibility invalid:', formData.visibility);
      setError('Giorni di visibilità devono essere maggiori di 0');
      return;
    }
    if (!formData.diarySupplyModes || formData.diarySupplyModes.length === 0) {
      console.log('❌ Validation failed: diarySupplyModes missing:', {
        diarySupplyModes: formData.diarySupplyModes,
        isArray: Array.isArray(formData.diarySupplyModes),
        length: formData.diarySupplyModes?.length,
        supplyModesOptions: supplyModes.map(m => ({ id: m.id, name: m.name }))
      });
      setError('Almeno un regime di erogazione è obbligatorio');
      return;
    }
    if (!formData.orders || formData.orders.length === 0) {
      console.log('❌ Validation failed: orders missing:', formData.orders);
      setError('Almeno una prestazione è obbligatoria');
      return;
    }
    if (!formData.priorityClasses || formData.priorityClasses.length === 0) {
      console.log('❌ Validation failed: priorityClasses missing:', formData.priorityClasses);
      setError('Almeno una classe di priorità è obbligatoria');
      return;
    }

    console.log('✅ All validations passed!');

    const diaryData = {
      ...formData,
      // NON includere ID per nuove agende - verrà generato dal backend
      ...(formData.id && { id: formData.id }), // Include ID solo se esiste già
      createdAt: diary?.createdAt || new Date(),
      updatedAt: new Date()
    };

    console.log('📝 DiaryForm: Calling onSave with diaryData:', diaryData);
    onSave(diaryData);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="lg"
      fullWidth
      className="diary-form-dialog"
    >
      <DialogTitle>
        {diary ? 'Modifica Agenda' : 'Nuova Agenda'}
      </DialogTitle>
      
      <DialogContent>
        {loadingError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {loadingError}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Caricamento dati in corso... Questo potrebbe richiedere alcuni secondi per sincronizzare i dati da RECUP.
          </Alert>
        )}

        <Box component="form" id="diary-form" onSubmit={handleSubmit}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Informazioni Base" />
            <Tab label="Configurazione" />
            <Tab label="Accesso e Tipologia" />
            <Tab label="Prestazioni e Priorità" />
            <Tab label="Avanzate" />
          </Tabs>
          
          {/* TAB 1: Informazioni Base */}
          {activeTab === 0 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Informazioni Generali</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome Agenda *"
                  value={formData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Codice Esterno *"
                  value={formData.externalCode || ''}
                  onChange={(e) => handleChange('externalCode', e.target.value)}
                  required
                  variant="outlined"
                  inputProps={{ maxLength: 20 }}
                  helperText="Massimo 20 caratteri"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Codice Interno"
                  value={formData.code || ''}
                  onChange={(e) => handleChange('code', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrizione"
                  value={formData.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo Agenda *</InputLabel>
                  <Select
                    value={formData.typeId || 2}
                    onChange={(e) => handleChange('typeId', e.target.value)}
                  >
                    <MenuItem value={2}>Tecnica</MenuItem>
                    <MenuItem value={3}>Laboratorio</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Dimensione Slot (min) *"
                  value={formData.slotSize || 15}
                  onChange={(e) => handleChange('slotSize', parseInt(e.target.value))}
                  required
                  variant="outlined"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Giorni Visibilità *"
                  value={formData.visibility || 30}
                  onChange={(e) => handleChange('visibility', parseInt(e.target.value))}
                  required
                  variant="outlined"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Unità e Strutture</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={executionUnits}
                  getOptionLabel={(option) => option.label || option.name || option.id}
                  value={executionUnits.find(unit => unit.id === formData.executionUnitId) || null}
                  onChange={(event, newValue) => handleChange('executionUnitId', newValue?.id || '')}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Unità Esecutiva *"
                      required
                      variant="outlined"
                      helperText="Seleziona l'unità esecutiva che gestirà l'agenda"
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{option.name}</Typography>
                          {option.hospitalName && (
                            <Typography variant="caption" color="textSecondary">
                              {option.hospitalName}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={authorities}
                  getOptionLabel={(option) => option.label || option.name || option.id}
                  value={authorities.find(auth => auth.id === formData.authorityId) || null}
                  onChange={(event, newValue) => handleChange('authorityId', newValue?.id || '')}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ente"
                      variant="outlined"
                      helperText="Seleziona l'ente di riferimento"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={hospitals}
                  getOptionLabel={(option) => option.label || option.name || option.id}
                  value={hospitals.find(hosp => hosp.id === formData.hospitalId) || null}
                  onChange={(event, newValue) => handleChange('hospitalId', newValue?.id || '')}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Presidio"
                      variant="outlined"
                      helperText="Seleziona il presidio ospedaliero"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={sites}
                  getOptionLabel={(option) => option.label || option.name || option.id}
                  value={sites.find(site => site.id === formData.siteId) || null}
                  onChange={(event, newValue) => handleChange('siteId', newValue?.id || '')}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sede"
                      variant="outlined"
                      helperText="Seleziona la sede operativa"
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
          
          {/* TAB 2: Configurazione */}
          {activeTab === 1 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Età e Disponibilità</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Età Minima (mesi) *"
                  value={formData.minAge || 0}
                  onChange={(e) => handleChange('minAge', parseInt(e.target.value))}
                  required
                  variant="outlined"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Età Massima (mesi) *"
                  value={formData.maxAge || 1200}
                  onChange={(e) => handleChange('maxAge', parseInt(e.target.value))}
                  required
                  variant="outlined"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Ritardo Disponibilità (ore)"
                  value={formData.availabilityDelay || 0}
                  onChange={(e) => handleChange('availabilityDelay', parseInt(e.target.value))}
                  variant="outlined"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Ore Ricerca Disponibilità"
                  value={formData.maxAvailabilityHours || 24}
                  onChange={(e) => handleChange('maxAvailabilityHours', parseInt(e.target.value))}
                  variant="outlined"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Durata Default Prestazioni (min)"
                  value={formData.defOrderDurationMins || 5}
                  onChange={(e) => handleChange('defOrderDurationMins', parseInt(e.target.value))}
                  variant="outlined"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Opzioni Generali</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.online || false}
                      onChange={(e) => handleChange('online', e.target.checked)}
                    />
                  }
                  label="Prenotabile Online"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.privateFlag || false}
                      onChange={(e) => handleChange('privateFlag', e.target.checked)}
                    />
                  }
                  label="Non Mostrare in Disponibilità"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.extraCup || false}
                      onChange={(e) => handleChange('extraCup', e.target.checked)}
                    />
                  }
                  label="Prenotabile Canali Esterni"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.forResidents || false}
                      onChange={(e) => handleChange('forResidents', e.target.checked)}
                    />
                  }
                  label="Riservata Residenti"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.overbook || false}
                      onChange={(e) => handleChange('overbook', e.target.checked)}
                    />
                  }
                  label="Consenti Overbook"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isInFirstAvail || true}
                      onChange={(e) => handleChange('isInFirstAvail', e.target.checked)}
                    />
                  }
                  label="Considera in Migliore Disponibilità"
                />
              </Grid>
            </Grid>
          )}
          
          {/* TAB 3: Accesso e Tipologia */}
          {activeTab === 2 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Modalità di Accesso</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Modalità Accesso</InputLabel>
                  <Select
                    value={formData.accessModeId || -1}
                    onChange={(e) => handleChange('accessModeId', e.target.value)}
                  >
                    <MenuItem value={-1}>Non Applicabile</MenuItem>
                    <MenuItem value={0}>A Piedi</MenuItem>
                    <MenuItem value={1}>In Macchina</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo Accesso</InputLabel>
                  <Select
                    value={formData.accessTypeId || -1}
                    onChange={(e) => handleChange('accessTypeId', e.target.value)}
                  >
                    <MenuItem value={-1}>Entrambi</MenuItem>
                    <MenuItem value={0}>Successivo</MenuItem>
                    <MenuItem value={1}>Primo Accesso</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Forzature e Liste di Attesa</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.freeForceEnabled || false}
                      onChange={(e) => handleChange('freeForceEnabled', e.target.checked)}
                    />
                  }
                  label="Abilita Forzatura Libera"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isWlReserved || false}
                      onChange={(e) => handleChange('isWlReserved', e.target.checked)}
                    />
                  }
                  label="Dedicata Lista di Attesa"
                />
              </Grid>
              
              {formData.freeForceEnabled && (
                <>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Giorni Min Forzatura"
                      value={formData.freeForceMinDistance || 0}
                      onChange={(e) => handleChange('freeForceMinDistance', parseInt(e.target.value))}
                      variant="outlined"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Giorni Max Forzatura"
                      value={formData.freeForceMaxDistance || 0}
                      onChange={(e) => handleChange('freeForceMaxDistance', parseInt(e.target.value))}
                      variant="outlined"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Forzature/Giorno"
                      value={formData.freeForceMaxNumber || 0}
                      onChange={(e) => handleChange('freeForceMaxNumber', parseInt(e.target.value))}
                      variant="outlined"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.freeForceNotesNeeded || false}
                          onChange={(e) => handleChange('freeForceNotesNeeded', e.target.checked)}
                        />
                      }
                      label="Note Obbligatorie"
                    />
                  </Grid>
                </>
              )}
              
              {formData.isWlReserved && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Giorni Permanenza Lista Attesa"
                    value={formData.wlReservedExpire || 0}
                    onChange={(e) => handleChange('wlReservedExpire', parseInt(e.target.value))}
                    variant="outlined"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              )}
            </Grid>
          )}
          
          {/* TAB 4: Prestazioni e Priorità */}
          {activeTab === 3 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="error">Sezioni Obbligatorie</Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Le sezioni seguenti sono obbligatorie per creare un'agenda funzionante.
                </Alert>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom color="error">
                  Regimi di Erogazione *
                </Typography>
                <Autocomplete
                  multiple
                  options={supplyModes}
                  getOptionLabel={(option) => option.label || option.name || option.id}
                  value={(() => {
                    const filteredValue = supplyModes.filter(mode => 
                      formData.diarySupplyModes.some((selected: any) => selected.supplyModeId === mode.id)
                    );
                    console.log('🔍 Supply Modes Autocomplete value:', {
                      allSupplyModes: supplyModes.map(m => ({ id: m.id, name: m.name })),
                      formDataSupplyModes: formData.diarySupplyModes,
                      filteredValue: filteredValue.map(m => ({ id: m.id, name: m.name }))
                    });
                    return filteredValue;
                  })()}
                  onChange={(event, newValue) => {
                    console.log('🔧 Supply Modes onChange:', {
                      selectedOptions: newValue,
                      mappedValues: newValue.map(mode => ({ supplyModeId: mode.id })),
                      currentFormData: formData.diarySupplyModes
                    });
                    handleArrayChange('diarySupplyModes', newValue.map(mode => ({ supplyModeId: mode.id })));
                  }}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Seleziona regimi di erogazione..."
                      helperText="Almeno un regime di erogazione è obbligatorio"
                      required
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom color="error">
                  Prestazioni *
                </Typography>
                <Autocomplete
                  multiple
                  options={availableExams}
                  getOptionLabel={(option) => option.label || option.name || option.id}
                  value={availableExams.filter(exam => 
                    formData.orders.some((selected: any) => selected.id === exam.id)
                  )}
                  onChange={(event, newValue) => {
                    handleArrayChange('orders', newValue.map(exam => ({ id: exam.id, duration: 5 })));
                  }}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Seleziona prestazioni..."
                      helperText="Almeno una prestazione è obbligatoria"
                      required
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{option.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {option.code} {option.description && ` - ${option.description}`}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom color="error">
                  Classi di Priorità *
                </Typography>
                <Autocomplete
                  multiple
                  options={priorityClasses}
                  getOptionLabel={(option) => option.label || option.name || option.id}
                  value={priorityClasses.filter(priority => 
                    formData.priorityClasses.includes(priority.id)
                  )}
                  onChange={(event, newValue) => {
                    handleArrayChange('priorityClasses', newValue.map(priority => priority.id));
                  }}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Seleziona classi di priorità..."
                      helperText="Almeno una classe di priorità è obbligatoria"
                      required
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const color = option.color || '#666666';
                      return (
                        <Chip
                          variant="outlined"
                          label={option.name}
                          {...getTagProps({ index })}
                          key={option.id}
                          style={{ borderColor: color, color: color }}
                        />
                      );
                    })
                  }
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: option.color || '#666666'
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">{option.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {option.maxDays ? `Max ${option.maxDays} giorni` : 'Nessun limite'}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    );
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Medici e Risorse (Opzionali)</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Medici Erogatori</Typography>
                <Autocomplete
                  multiple
                  options={availableDoctors}
                  getOptionLabel={(option) => option.label || `${option.lastName} ${option.firstName}` || option.id}
                  value={availableDoctors.filter(doctor => 
                    formData.doctors?.includes(doctor.id)
                  )}
                  onChange={(event, newValue) => {
                    handleArrayChange('doctors', newValue.map(doctor => doctor.id));
                    // Imposta il primo medico selezionato come titolare dell'agenda
                    if (newValue.length > 0) {
                      handleChange('holderId', newValue[0].id);
                    } else {
                      handleChange('holderId', '');
                    }
                  }}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Seleziona medici erogatori..."
                      helperText="Il primo medico selezionato diventerà il titolare dell'agenda"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant={index === 0 ? "filled" : "outlined"}
                        color={index === 0 ? "primary" : "default"}
                        label={`${option.lastName} ${option.firstName}${index === 0 ? ' (Titolare)' : ''}`}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {option.lastName} {option.firstName}
                          </Typography>
                          {option.specialization && (
                            <Typography variant="caption" color="textSecondary">
                              {option.specialization}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                />
              </Grid>
              
              {formData.holderId && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <strong>Titolare Agenda:</strong> {availableDoctors.find(d => d.id === formData.holderId)?.label || formData.holderId}
                  </Alert>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="IDs Risorse (separati da virgola)"
                  value={formData.resources?.join(', ') || ''}
                  onChange={(e) => {
                    const ids = e.target.value.split(',').map((id: string) => id.trim()).filter((id: string) => id);
                    handleArrayChange('resources', ids);
                  }}
                  variant="outlined"
                  helperText="Inserire gli ID delle risorse associate"
                />
              </Grid>
            </Grid>
          )}
          
          {/* TAB 5: Avanzate */}
          {activeTab === 4 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Configurazioni Speciali</Typography>
              </Grid>
              
              {formData.typeId === 3 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>Configurazione Laboratorio</Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="ID Unità Esecutiva Lab"
                      value={formData.executionUnitIdLab || ''}
                      onChange={(e) => handleChange('executionUnitIdLab', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="ID Punto Prelievo"
                      value={formData.deliveryPlaceId || ''}
                      onChange={(e) => handleChange('deliveryPlaceId', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Coperture e Diagnosi</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.allCovers || true}
                      onChange={(e) => handleChange('allCovers', e.target.checked)}
                    />
                  }
                  label="Tutte le Coperture"
                />
              </Grid>
              
              {!formData.allCovers && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="IDs Coperture Specifiche (separati da virgola)"
                    value={formData.coverIds?.join(', ') || ''}
                    onChange={(e) => {
                      const ids = e.target.value.split(',').map((id: string) => id.trim()).filter((id: string) => id);
                      handleArrayChange('coverIds', ids);
                    }}
                    variant="outlined"
                  />
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="IDs Diagnosi (separati da virgola)"
                  value={formData.diagnosis?.join(', ') || ''}
                  onChange={(e) => {
                    const ids = e.target.value.split(',').map((id: string) => id.trim()).filter((id: string) => id);
                    handleArrayChange('diagnosis', ids);
                  }}
                  variant="outlined"
                  helperText="Inserire gli ID delle diagnosi associate"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="IDs ASL Associate (separati da virgola)"
                  value={formData.diaryAsls?.join(', ') || ''}
                  onChange={(e) => {
                    const ids = e.target.value.split(',').map((id: string) => id.trim()).filter((id: string) => id);
                    handleArrayChange('diaryAsls', ids);
                  }}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>Date e Stato</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data Inizio"
                  value={formData.startDate || ''}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data Fine"
                  value={formData.endDate || ''}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={formData.status || 'active'}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    <MenuItem value="active">Attiva</MenuItem>
                    <MenuItem value="inactive">Inattiva</MenuItem>
                    <MenuItem value="suspended">Sospesa</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Annulla
        </Button>
        <Button 
          type="submit"
          form="diary-form"
          variant="contained" 
          color="primary"
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiaryForm;